import { apiErrorResponseSchema } from "@portfolio-demos/demo-core";
import { z } from "zod";

export const resultSchema = z.object({
  candidateCode: z.string(),
  candidateName: z.string(),
  partyAlias: z.string(),
  partyCode: z.string(),
  votesAmount: z.number().int().nonnegative(),
  votePercentage: z.number().min(0).max(100),
  color: z.string(),
  colorSlot: z.number().int().min(1).max(6),
});

export const electionResultsResponseSchema = z.object({
  results: z.array(resultSchema),
  contest: z.object({
    contestCode: z.string(),
    contestName: z.string(),
    type: z.string(),
    seats: z.number().int().positive(),
  }),
  scope: z.object({
    regionName: z.string().nullable(),
    provinceName: z.string().nullable(),
    municipalityName: z.string().nullable(),
  }),
  synthetic: z.boolean(),
  dataVersion: z.string(),
});

export const statsSchema = z.object({
  precinctsProcessed: z.number().int().nonnegative(),
  totalPrecincts: z.number().int().positive(),
  precinctsProcessedPercentage: z.string(),
  totalVotes: z.number().int().nonnegative(),
  registeredVoters: z.number().int().positive(),
  voterTurnoutPercentage: z.string(),
  lastUpdated: z.string(),
  synthetic: z.boolean(),
  dataVersion: z.string(),
});

export const statisticsSchema = z.object({
  receptionDate: z.string(),
  votersPercentage: z.number(),
  totalSubmittedPerRegion: z.array(
    z.object({
      regionName: z.string(),
      votersPercentage: z.number(),
      receptionDate: z.string(),
    }),
  ),
  totalSubmittedPerProvince: z.array(
    z.object({
      regionName: z.string(),
      provinceName: z.string(),
      votersPercentage: z.number(),
      receptionDate: z.string(),
    }),
  ),
  synthetic: z.boolean(),
  dataVersion: z.string(),
});

export const provinceTupleSchema = z.tuple([
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.string(),
  z.number(),
  z.number(),
  z.number(),
  z.number(),
  z.number(),
  z.number(),
  z.string(),
]);

export const byProvinceResponseSchema = z.object({
  results: z.array(provinceTupleSchema),
  synthetic: z.boolean(),
  dataVersion: z.string(),
});

const reportingSchema = z.object({
  processed: z.number().int().nonnegative(),
  total: z.number().int().positive(),
  percent: z.number().min(0).max(100),
});

export const regionsSchema = z.array(
  z.object({
    name: z.string(),
    displayName: z.string(),
    code: z.string(),
    path: z.string(),
    center: z.object({ x: z.number(), y: z.number() }),
    reporting: reportingSchema,
    provinces: z.array(
      z.object({
        name: z.string(),
        percent: z.number(),
        precincts: z.string(),
      }),
    ),
  }),
);

export const regionDetailSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  code: z.string(),
  reporting: reportingSchema,
  provinces: z.array(
    z.object({
      name: z.string(),
      code: z.string(),
      ballots: z.number(),
      reporting: reportingSchema,
      municipalities: z.array(z.object({ name: z.string(), weight: z.number() })),
    }),
  ),
});

export const childrenResponseSchema = z.object({
  children: z.array(z.string()),
});
export const contestsResponseSchema = z.object({
  contests: z.array(z.object({ contestCode: z.string(), contestName: z.string() })),
});

export const searchResponseSchema = z.object({
  results: z.array(
    z.object({
      type: z.string(),
      value: z.string(),
      region: z.string(),
      province: z.string(),
      city: z.string(),
      barangay: z.string(),
      candidateName: z.string(),
      candidatePartyAlias: z.string(),
      candidateContestName: z.string(),
      link: z.string().nullable(),
    }),
  ),
  synthetic: z.literal(true),
  dataVersion: z.string(),
});

export type ElectionResult = z.infer<typeof resultSchema>;
export type ElectionResultsResponse = z.infer<typeof electionResultsResponseSchema>;
export type Stats = z.infer<typeof statsSchema>;
export type RegionSummary = z.infer<typeof regionsSchema>[number];
export type ProvinceTuple = z.infer<typeof provinceTupleSchema>;
export type SearchResult = z.infer<typeof searchResponseSchema>["results"][number];

export const fetchJson = async <Schema extends z.ZodType>(
  input: string,
  schema: Schema,
  signal?: AbortSignal,
): Promise<z.infer<Schema>> => {
  const response = await fetch(input, {
    headers: { accept: "application/json" },
    ...(signal ? { signal } : {}),
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const parsedError = apiErrorResponseSchema.safeParse(body);
    throw new Error(
      parsedError.success
        ? parsedError.data.error.message
        : `Request failed with ${response.status}.`,
    );
  }
  return schema.parse(body);
};
